import React, { useCallback, useMemo, useEffect, useState } from 'react';
import css from './styles.module.scss';
import { Button, Typography, Box, Paper, IconButton, Divider, Drawer } from '@material-ui/core';
import { StepControls } from '~/common/step-controls';
import { useSelector } from 'react-redux';
import { selectNewDeal, getDealFormatList, getDealCodeList, getDealRegionsList } from '~/features/dashboard/store/selectors';
import { useForm, Controller } from 'react-hook-form';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { IRecoupableFormInputs, IRoyaltyRateFormInputs } from '~/models/deals/new-deal/formInputs';
import {
    createNewDealStepSixThunk,
    createNewDealStepSixRoyaltyDeleteThunk,
    createNewDealStepSixRoyaltyThunk,
    getDealFormats,
    getDealCodes,
    getDealRegions,
} from '~/features/dashboard/store/actions.thunks';
import * as CreatorsListFeature from '~/features/creators-list';
import { useThunkDispatch } from '~/utils/hooks/useThunkDispatch';
import { useAppRoute } from '~/utils/hooks/useAppRoute';
import { AutocompleteRenderInputParams } from '@material-ui/lab/Autocomplete/Autocomplete';
import { createSongTags } from '~/features/dashboard/store/actions';
import { notifyError, notifySuccess } from '~/common/notifications';
import classNames from 'classnames';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import EditIcon from '@material-ui/icons/Edit';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import { TextFieldOutlined } from '~/common/text-field-outlined';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import {
    FormatsListDrawer,
    CodesListDrawer,
    ItemType,
    RegionsListDrawer,
} from '~/features/dashboard/components/editable-list-drawer';

interface Step_6Props {
    prevStep: () => void;
}

enum SECTION_TYPES {
    RECOUPABLES,
    ROYALTY,
}

enum DRAWER_TYPES {
    FORMAT = 'FORMAT',
    REGION = 'REGION',
    CODE = 'CODE',
}

export const Step_6: React.FC<Step_6Props> = ({ prevStep }) => {
    const route = useAppRoute();

    const [activeSection, setActiveSection] = useState<number>(SECTION_TYPES.ROYALTY);

    const allRoyaltyRateFormats = useSelector(getDealFormatList);
    const royaltyRateFormats: ItemType[] = useMemo(() => {
        if (allRoyaltyRateFormats && allRoyaltyRateFormats.length) {
            const items: ItemType[] = allRoyaltyRateFormats.map(i => ({ id: i.customField ? i.formatId : 0, title: i.formatDesc }));
            return items.filter(i => i.title !== 'HEADLINE RATE');
        }
        return [];
    }, [allRoyaltyRateFormats]);

    const allRecoupableCodes = useSelector(getDealCodeList);
    const recoupableCodes: ItemType[] = useMemo(() => {
        if (allRecoupableCodes && allRecoupableCodes.length) {
            return allRecoupableCodes.map(i => ({ id: i.customField ? i.id : 0, title: i.analysisText }));
        }
        return [];
    }, [allRecoupableCodes]);

    const allRegions = useSelector(getDealRegionsList);
    const countryList: ItemType[] = useMemo(() => {
        if (allRegions && allRegions.length) {
            return allRegions.map(i => ({ id: i.customField ? i.id : 0, title: i.regionText }));
        }
        return [];
    }, [allRegions]);

    const newDealState = useSelector(selectNewDeal);

    const initialRoyaltyRate: IRoyaltyRateFormInputs = {
        format: '',
        region: '',
        rate: 0,
        contractRateID: 0,
        contractRefNum: '',
        ifCustom: false,
        customInput: '',
    };

    const initialRecoupable: IRecoupableFormInputs = {
        contractRecoupCode: '',
        region: '',
        rate: 0,
        contractRefNum: '',
        contractRecoupID: 0,
        ifCustom: false,
        customInput: '',
    };

    const defaultValues = useMemo(
        () => ({
            royaltyRates: newDealState?.royaltyRates.length
                ? newDealState?.royaltyRates.map(item => ({
                      format: item.contractRateFormat,
                      region: !countryList.find(i => i.title === item.contractRateRegion) && item.contractRateRegion !== '' ? 'CUSTOM' : item.contractRateRegion,
                      rate: item.contractRateRate,
                      contractRateID: Number(item.contractRateID),
                      contractRefNum: item.contractRefNum,
                      customInput: item.contractRateRegion,
                      ifCustom: !countryList.find(i => i.title === item.contractRateRegion) && item.contractRateRegion !== '' ? true : false,
                  }))
                : [{ ...initialRoyaltyRate }],
            recoupables: newDealState?.recoupables.length
                ? newDealState?.recoupables.map(item => ({
                      contractRecoupCode: item.contractRecoupCode,
                      region: !countryList.find(i => i.title === item.contractRecoupRegion) && item.contractRecoupRegion !== '' ? 'CUSTOM' : item.contractRecoupRegion,
                      rate: item.contractRecoupRate,
                      contractRecoupID: Number(item.contractRecoupID),
                      contractRefNum: item.contractRefNum,
                      customInput: item.contractRecoupRegion,
                      ifCustom: !countryList.find(i => i.title === item.contractRecoupRegion) && item.contractRecoupRegion !== '' ? true : false,
                  }))
                : [{ ...initialRecoupable }],
        }),
        [initialRecoupable, initialRoyaltyRate, newDealState?.recoupables, newDealState?.royaltyRates] // eslint-disable-line react-hooks/exhaustive-deps
    );

    const { register, control, handleSubmit, errors, setValue, reset, getValues, watch } = useForm<any>({
        defaultValues,
    });

    const dispatch = useThunkDispatch();

    const onSubmit = useCallback(
        async (data: any) => {
            if (activeSection === SECTION_TYPES.ROYALTY) {
                setActiveSection(SECTION_TYPES.RECOUPABLES);
            } else {
                await dispatch(createNewDealStepSixThunk({ ...data }));
            }
        },
        [dispatch, activeSection]
    );

    useEffect(() => {
        dispatch(getDealFormats()).then(data => {
            if (data && data.error) {
                route(CreatorsListFeature.FEATURE_ROUTER_PATH);
            }
        });
    }, [dispatch, route]);

    useEffect(() => {
        dispatch(getDealCodes()).then(data => {
            if (data && data.error) {
                route(CreatorsListFeature.FEATURE_ROUTER_PATH);
            }
        });
    }, [dispatch, route]);

    useEffect(() => {
        dispatch(getDealRegions()).then(data => {
            if (data && data.error) {
                route(CreatorsListFeature.FEATURE_ROUTER_PATH);
            }
        });
    }, [dispatch, route]);

    let royaltyRateIniitial = {
        contractRateFormat: '',
        contractRateID: 0,
        contractRateRate: 0,
        contractRateRegion: '',
        contractRateCustomInput: '',
        contractRefNum: newDealState?.contractRefNum || '',
    };
    let recoupableInitials = {
        contractRecoupCode: '',
        contractRecoupID: 0,
        contractRecoupRate: 0,
        contractRecoupRegion: '',
        contractRecoupCustomInput: '',
        contractRefNum: newDealState?.contractRefNum || '',
    };

    const [royaltyRateList, setRoyaltyRateList] = useState<any>([]);
    const [recoupableRateList, setRecoupableRateList] = useState<any>([]);
    const [formLoading, setFormLoading] = useState(true);
    const [selectedRoyaltyRate, setSelectedRoyaltyRate] = useState<any>(royaltyRateIniitial);
    const [selectedRecoupableRate, setSelectedRecoupableRate] = useState<any>(recoupableInitials);

    useEffect(() => {
        setRoyaltyRateList(newDealState?.royaltyRates);
        setRecoupableRateList(newDealState?.recoupables);
        setFormLoading(false);
    }, [newDealState]);

    const checkIfCustom = (region: any) => {
        if (countryList.find(i => i.title === region)) {
            return region;
        } else {
            return 'CUSTOM';
        }
    };
    const setIfCustom = (region: any) => {
        if (countryList.find(i => i.title === region)) {
            return '';
        } else {
            return region;
        }
    };

    const EditRoyalty = (id: number) => {
        let selectedRoyalty = royaltyRateList?.find((item: any) => Number(item.contractRateID) === id);
        setSelectedRoyaltyRate({
            contractRateFormat: selectedRoyalty.contractRateFormat,
            contractRateID: selectedRoyalty.contractRateID,
            contractRateRate: selectedRoyalty.contractRateRate,
            contractRateRegion: countryList.find(i => i.title === selectedRoyalty.contractRateRegion) ? selectedRoyalty.contractRateRegion : 'CUSTOM',
            contractRateCustomInput: countryList.find(i => i.title === selectedRoyalty.contractRateRegion) ? '' : selectedRoyalty.contractRateRegion,
            contractRefNum: selectedRoyalty.contractRefNum,
        });

        setValue('contractRateFormat', selectedRoyalty.contractRateFormat);
        setValue('contractRateID', selectedRoyalty.contractRateID);
        setValue('contractRateRate', selectedRoyalty.contractRateRate);
        setValue('contractRateRegion', countryList.find(i => i.title === selectedRoyalty.contractRateRegion) ? selectedRoyalty.contractRateRegion : 'CUSTOM');
        setValue('contractRateCustomInput', countryList.find(i => i.title === selectedRoyalty.contractRateRegion) ? '' : selectedRoyalty.contractRateRegion);
        setValue('contractRefNum', selectedRoyalty.contractRefNum);
    };

    const EditRecoupable = (id: number) => {
        let selectedRecoupble = recoupableRateList?.find((item: any) => Number(item.contractRecoupID) === id);
        setSelectedRecoupableRate({
            contractRecoupCode: selectedRecoupble.contractRecoupCode,
            contractRecoupID: selectedRecoupble.contractRecoupID,
            contractRecoupRate: selectedRecoupble.contractRecoupRate,
            contractRecoupRegion: countryList.find(i => i.title === selectedRecoupble.contractRecoupRegion) ? selectedRecoupble.contractRecoupRegion : 'CUSTOM',
            contractRecoupCustomInput: countryList.find(i => i.title === selectedRecoupble.contractRecoupRegion) ? '' : selectedRecoupble.contractRecoupRegion,
            contractRefNum: selectedRecoupble.contractRefNum,
        });

        setValue('contractRecoupCode', selectedRecoupble.contractRecoupCode);
        setValue('contractRecoupID', selectedRecoupble.contractRecoupID);
        setValue('contractRecoupRate', selectedRecoupble.contractRecoupRate);
        setValue('contractRecoupRegion', countryList.find(i => i.title === selectedRecoupble.contractRecoupRegion) ? selectedRecoupble.contractRecoupRegion : 'CUSTOM');
        setValue('contractRecoupCustomInput', countryList.find(i => i.title === selectedRecoupble.contractRecoupRegion) ? '' : selectedRecoupble.contractRecoupRegion);
        setValue('contractRefNum', selectedRecoupble.contractRefNum);
    };

    const saveDataRoyalty = () => {
        let recoupableArray = [...recoupableRateList];
        let tempArray: any = [...royaltyRateList];

        let obj = {
            contractRateFormat: getValues('contractRateFormat'),
            contractRateID: Number(getValues('contractRateID')),
            contractRateRate: getValues('contractRateRate'),
            contractRateRegion: getValues('contractRateRegion') === 'CUSTOM' ? getValues('contractRateCustomInput') : getValues('contractRateRegion'),
            contractRefNum: getValues('contractRefNum'),
        };

        if (obj.contractRateFormat === '' || obj.contractRateFormat === null) {
            notifyError({
                title: 'Error',
                message: 'Please select Royalty rate format',
            });
        } else {
            if (Number(obj.contractRateID) !== 0) {
                let index = tempArray?.findIndex((item: any) => Number(item.contractRateID) === Number(obj.contractRateID));
                tempArray[index].contractRateFormat = obj.contractRateFormat;
                tempArray[index].contractRateID = obj.contractRateID;
                tempArray[index].contractRateRate = obj.contractRateRate;
                tempArray[index].contractRateRegion = obj.contractRateRegion;
                tempArray[index].contractRefNum = obj.contractRefNum;

                setRoyaltyRateList(tempArray);
            } else {
                tempArray.push(obj);

                setRoyaltyRateList([...royaltyRateList, obj]);
            }
            dispatch(createNewDealStepSixRoyaltyThunk({ royaltyRates: tempArray, recoupables: recoupableArray })).then(() => {
                notifySuccess({
                    title: 'Success',
                    message: 'Royalty format data added successfullly',
                });
            });

            reset(royaltyRateIniitial);
        }
    };

    const saveDataRecoupable = () => {
        let royaltyArray = [...royaltyRateList];
        let tempArray: any = [...recoupableRateList];

        let obj = {
            contractRecoupCode: getValues('contractRecoupCode'),
            contractRecoupID: Number(getValues('contractRecoupID')),
            contractRecoupRate: getValues('contractRecoupRate'),
            contractRecoupRegion: getValues('contractRecoupRegion') === 'CUSTOM' ? getValues('contractRecoupCustomInput') : getValues('contractRecoupRegion'),
            contractRefNum: getValues('contractRefNum'),
        };

        if (obj.contractRecoupCode === '' || obj.contractRecoupCode === null) {
            notifyError({
                title: 'Error',
                message: 'Please select recoupable code',
            });
        } else {
            if (Number(obj.contractRecoupID) !== 0) {
                let index = tempArray?.findIndex((item: any) => Number(item.contractRecoupID) === Number(obj.contractRecoupID));
                tempArray[index].contractRecoupCode = obj.contractRecoupCode;
                tempArray[index].contractRecoupID = obj.contractRecoupID;
                tempArray[index].contractRecoupRate = obj.contractRecoupRate;
                tempArray[index].contractRecoupRegion = obj.contractRecoupRegion;
                tempArray[index].contractRefNum = obj.contractRefNum;

                setRecoupableRateList(tempArray);
            } else {
                tempArray.push(obj);

                setRecoupableRateList([...recoupableRateList, obj]);
            }
            dispatch(createNewDealStepSixRoyaltyThunk({ royaltyRates: royaltyArray, recoupables: tempArray })).then(() => {
                notifySuccess({
                    title: 'Success',
                    message: 'Recouble Data added successfullly',
                });
            });

            reset(recoupableInitials);
        }
    };

    const deleteItem = async (type: any, id: any) => {
        if (newDealState) {
            let data1: any = {
                gSongRef: '12sd',
            };

            await dispatch(createNewDealStepSixRoyaltyDeleteThunk({ type: type, id: id }));
            await dispatch(createSongTags.success(data1));
        }
    };

    let watchRoyaltyFormat = watch('contractRateFormat');
    let watchRoyaltyRegion = watch('contractRateRegion');
    let watchcontractRecoupCode = watch('contractRecoupCode');
    let watchcontractRecoupRegion = watch('contractRecoupRegion');

    const [drawerData, setDrawerData] = useState<{ isOpen: boolean; type?: DRAWER_TYPES; meta?: any }>({ isOpen: false });
    const closeDrawer = () => {
        if (drawerData.type === DRAWER_TYPES.FORMAT) {
            dispatch(getDealFormats());
        }
        if (drawerData.type === DRAWER_TYPES.REGION) {
            dispatch(getDealRegions());
        }
        if (drawerData.type === DRAWER_TYPES.CODE) {
            dispatch(getDealCodes());
        }
        setDrawerData({ isOpen: false, type: undefined, meta: undefined });
    };
    const openDrawer = (type: DRAWER_TYPES, meta?: any) => {
        setDrawerData({ isOpen: true, type, meta });
    };
    const openFormatDrawer = () => {
        openDrawer(DRAWER_TYPES.FORMAT);
    };
    const openRegionDrawer = () => {
        openDrawer(DRAWER_TYPES.REGION);
    };
    const openCodeDrawer = () => {
        openDrawer(DRAWER_TYPES.CODE);
    };

    const onPrevStep = () => {
        if (activeSection === SECTION_TYPES.RECOUPABLES) {
            setActiveSection(SECTION_TYPES.ROYALTY);
        } else {
            prevStep();
        }
    };


    return formLoading ? (
        <></>
    ) : (
        <Box className={css.module}>
            <Box className={css.wrapper}>
                <Paper square elevation={0} className={classNames(css.paper, css.header)}>
                    <Typography align={'left'} variant={'h5'}>
                        {newDealState?.contractTitle}
                    </Typography>
                </Paper>
                <form className={css.form} onSubmit={handleSubmit(onSubmit)}>
                    <div className={css.dataSection}>
                        <Box display="flex" justifyContent="center">
                            <ToggleButtonGroup
                                value={Number(activeSection)}
                                exclusive
                                onChange={(omit, value) => setActiveSection(value)}
                                aria-label="toggle data view"
                                className={css.toggleButtonGroup}
                            >
                                <ToggleButton value={SECTION_TYPES.ROYALTY} aria-label="contacts view">
                                    Royalty Rate
                                </ToggleButton>
                                <ToggleButton value={SECTION_TYPES.RECOUPABLES} aria-label="address view">
                                    Recoupables
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                        <div className={classNames(css.dataFormSection, Number(activeSection) !== SECTION_TYPES.ROYALTY && css.hidden)}>
                            <div className={css.dataFormSectionBody}>
                                <div className={css.dataFormInput}>
                                    <Controller
                                        defaultValue={watchRoyaltyFormat || ''}
                                        render={props => (
                                            <Autocomplete
                                                {...props}
                                                options={royaltyRateFormats.map(i => i.title || '')}
                                                fullWidth
                                                defaultValue={watchRoyaltyFormat}
                                                renderInput={(p: AutocompleteRenderInputParams) => (
                                                    <div>
                                                        <TextFieldOutlined
                                                            {...p}
                                                            label="Formats / Source Code"
                                                        />
                                                    </div>
                                                )}
                                                onChange={(_, data) => props.onChange(data)}
                                            />
                                        )}
                                        name={`contractRateFormat`}
                                        control={control}
                                    />
                                    <IconButton
                                        className={css.editButton}
                                        size="small"
                                        title="Edit Formats / Source Code"
                                        onClick={openFormatDrawer}
                                    >
                                        <EditIcon fontSize="small"/>
                                    </IconButton>
                                </div>
                                <div className={css.hidden}>
                                    <input type="hidden" name="contractRateID" ref={register} defaultValue={selectedRoyaltyRate.contractRateID} />
                                    <input type="hidden" name="contractRefNum" ref={register} defaultValue={newDealState?.contractRefNum || 0} />
                                </div>
                                <div className={css.dataFormInput}>
                                    <Controller
                                        defaultValue={watchRoyaltyRegion || ''}
                                        render={props => (
                                            <Autocomplete
                                                {...props}
                                                options={countryList.map(i => i.title)}
                                                fullWidth
                                                defaultValue={watchRoyaltyRegion}
                                                value={props.value}
                                                renderInput={(p: AutocompleteRenderInputParams) => (
                                                    <TextFieldOutlined
                                                        {...p}
                                                        label="Region"
                                                    />
                                                )}
                                                onChange={(_, data) => props.onChange(data)}
                                            />
                                        )}
                                        name={`contractRateRegion`}
                                        control={control}
                                    />
                                    <IconButton
                                        className={css.editButton}
                                        size="small"
                                        title="Edit Regions"
                                        onClick={openRegionDrawer}
                                    >
                                        <EditIcon fontSize="small"/>
                                    </IconButton>
                                </div>
                                <div className={classNames(css.dataFormInput, watchRoyaltyRegion !== 'CUSTOM' && css.hidden)}>
                                    <TextFieldOutlined
                                        className={css.dataFormTextInput}
                                        type="text"
                                        label="Custom Input"
                                        name="contractRateCustomInput"
                                        inputRef={register}
                                        disabled={watchRoyaltyRegion !== 'CUSTOM'}
                                        defaultValue={selectedRoyaltyRate.contractRateCustomInput}
                                    />
                                </div>
                                <div className={css.dataFormInput}>
                                    <TextFieldOutlined
                                        className={css.dataFormTextInput}
                                        type="text"
                                        label="Royalty Rate"
                                        name="contractRateRate"
                                        inputRef={register}
                                        defaultValue={selectedRoyaltyRate.contractRateRate}
                                    />
                                </div>
                                <Box className={css.dataFormInput} flexGrow="1" justifyContent="flex-end">
                                    <Button className={classNames(css.button, css.saveButton)} variant="contained" color="primary" onClick={() => saveDataRoyalty()}>
                                        Save
                                    </Button>
                                </Box>
                            </div>
                        </div>
                        <div className={classNames(css.dataFormSection, Number(activeSection) !== SECTION_TYPES.RECOUPABLES && css.hidden)}>
                            <div className={css.dataFormSectionBody}>
                                <div className={css.dataFormInput}>
                                    <Controller
                                        defaultValue={watchcontractRecoupCode || ''}
                                        render={props => (
                                            <Autocomplete
                                                {...props}
                                                options={recoupableCodes.map(i => i.title || '')}
                                                fullWidth
                                                defaultValue={watchcontractRecoupCode}
                                                value={props.value}
                                                renderInput={(p: AutocompleteRenderInputParams) => (
                                                    <TextFieldOutlined
                                                        {...p}
                                                        label="Code"
                                                    />
                                                )}
                                                onChange={(_, data) => props.onChange(data)}
                                            />
                                        )}
                                        name={`contractRecoupCode`}
                                        control={control}
                                    />
                                    <IconButton
                                        className={css.editButton}
                                        size="small"
                                        title="Edit Codes"
                                        onClick={openCodeDrawer}
                                    >
                                        <EditIcon fontSize="small"/>
                                    </IconButton>
                                </div>
                                <div className={css.dataFormInput}>
                                    <Controller
                                        defaultValue={watchcontractRecoupRegion || ''}
                                        render={props => (
                                            <Autocomplete
                                                {...props}
                                                options={countryList.map(i => i.title)}
                                                fullWidth
                                                defaultValue={watchcontractRecoupRegion}
                                                value={props.value}
                                                renderInput={(p: AutocompleteRenderInputParams) => (
                                                    <TextFieldOutlined
                                                        {...p}
                                                        label="Region"
                                                    />
                                                )}
                                                onChange={(_, data) => props.onChange(data)}
                                            />
                                        )}
                                        name={`contractRecoupRegion`}
                                        control={control}
                                    />
                                    <IconButton
                                        className={css.editButton}
                                        size="small"
                                        title="Edit Regions"
                                        onClick={openRegionDrawer}
                                    >
                                        <EditIcon fontSize="small"/>
                                    </IconButton>
                                </div>
                                <div className={classNames(css.dataFormInput, watchcontractRecoupRegion !== 'CUSTOM' && css.hidden)}>
                                    <TextFieldOutlined
                                        className={css.dataFormTextInput}
                                        type="text"
                                        label="Custom Input"
                                        name="contractRecoupCustomInput"
                                        inputRef={register}
                                        disabled={watchcontractRecoupRegion !== 'CUSTOM'}
                                        defaultValue={selectedRecoupableRate.contractRecoupCustomInput}
                                    />
                                </div>
                                <div className={css.dataFormInput}>
                                    <TextFieldOutlined
                                        className={css.dataFormTextInput}
                                        type="text"
                                        label="Recoupable Rate"
                                        name="contractRecoupRate"
                                        inputRef={register}
                                        defaultValue={selectedRecoupableRate.contractRecoupRate}
                                    />
                                </div>
                                <div className={css.hidden}>
                                    <input type="hidden" name="contractRecoupID" ref={register} defaultValue={selectedRecoupableRate.contractRecoupID} />
                                    <input type="hidden" name="contractRefNum" ref={register} defaultValue={newDealState?.contractRefNum || 0} />
                                </div>
                                <Box className={css.dataFormInput} flexGrow="1" justifyContent="flex-end">
                                    <Button className={classNames(css.button, css.saveButton)} variant="contained" color="primary" onClick={() => saveDataRecoupable()}>
                                        Save
                                    </Button>
                                </Box>
                            </div>
                        </div>
                        <table className={classNames(css.dataTable, Number(activeSection) !== SECTION_TYPES.ROYALTY && css.hidden)} cellPadding="0" cellSpacing="0">
                            <thead>
                                <tr>
                                    <td>Format</td>
                                    <td>Regions</td>
                                    <td>Custom Input</td>
                                    <td>Royalty Rate</td>
                                    <td>
                                        <Box textAlign="center">
                                            Action
                                        </Box>
                                    </td>
                                </tr>
                            </thead>
                            <tbody>
                                {newDealState?.royaltyRates?.map((item: any, index: number) => (
                                    <tr>
                                        <td>{item.contractRateFormat}</td>
                                        <td>{checkIfCustom(item.contractRateRegion)}</td>
                                        <td>{setIfCustom(item.contractRateRegion)}</td>
                                        <td>{item.contractRateRate}</td>
                                        <td>
                                            <Box textAlign="center">
                                                <DeleteOutlineIcon className={css.deleteIcon} onClick={() => deleteItem('royalty', item.contractRateID)} />
                                                <EditIcon className={css.editIcon} onClick={() => EditRoyalty(item.contractRateID)} />
                                            </Box>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <table className={classNames(css.dataTable, Number(activeSection) !== SECTION_TYPES.RECOUPABLES && css.hidden)} cellPadding="0" cellSpacing="0">
                            <thead>
                                <tr>
                                    <td>Code</td>
                                    <td>Regions</td>
                                    <td>Custom Input</td>
                                    <td>Recoupable Rate</td>
                                    <td>
                                        <Box width="100%" textAlign="center">
                                            Actions
                                        </Box>
                                    </td>
                                </tr>
                            </thead>
                            <tbody>
                                {newDealState?.recoupables?.map((item: any, index: number) => (
                                    <tr>
                                        <td>{item.contractRecoupCode}</td>
                                        <td>{checkIfCustom(item.contractRecoupRegion)}</td>
                                        <td>{setIfCustom(item.contractRecoupRegion)}</td>
                                        <td>{item.contractRecoupRate}</td>
                                        <td>
                                            <Box width="100%" textAlign="center">
                                                <DeleteOutlineIcon className={css.deleteIcon} onClick={() => deleteItem('recoupable', item.contractRecoupID)} />
                                                <EditIcon className={css.editIcon} onClick={() => EditRecoupable(item.contractRecoupID)} />
                                            </Box>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <StepControls onPrevClick={onPrevStep} errors={errors} />
                </form>
            </Box>
            <Drawer variant="persistent" anchor="right" open={drawerData.isOpen} id="deal-edit-step-6-drawer" className={css.rightDrawer}>
                <Box className={css.contentWrapper}>
                    <Box className={css.closeDrawer}>
                        <IconButton onClick={closeDrawer}>
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography style={{ fontSize: '1rem' }}>Close</Typography>
                    </Box>
                    <Divider />
                    {drawerData.type === DRAWER_TYPES.FORMAT && (
                        <FormatsListDrawer
                            items={royaltyRateFormats}
                        />
                    )}
                    {drawerData.type === DRAWER_TYPES.REGION && (
                        <RegionsListDrawer
                            items={countryList}
                        />
                    )}
                    {drawerData.type === DRAWER_TYPES.CODE && (
                        <CodesListDrawer
                            items={recoupableCodes}
                        />
                    )}
                </Box>
            </Drawer>
        </Box>
    );
};
